import { useEffect, useMemo, useRef, useState } from 'react';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { useNikkeCatalog } from '../../nikke-catalog/hooks/useNikkeCatalog.js';
import { NikkeDeckDisplay } from '../../nikke-catalog/shared/NikkeDeckDisplay.jsx';
import {
  filterRecommendedDecks,
  RECOMMENDED_DECK_FILTERS,
  RECOMMENDED_DECK_ELEMENTS,
} from '../domain/recommendedDecks.js';
import { useRecommendedDecks } from '../hooks/useRecommendedDecks.js';

function chunkSize(mobile) {
  return mobile ? 2 : 4;
}

function eagerCardCount(mobile) {
  return mobile ? 1 : 2;
}

export function RecommendedDeckLoadDialog({
  mobile = false,
  unionId = '',
  defaultElement = '전체',
  onApply,
  onClose,
}) {
  const decks = useRecommendedDecks({ unionId });
  const { catalog, loading: catalogLoading } = useNikkeCatalog();
  const initialElement = RECOMMENDED_DECK_ELEMENTS.includes(defaultElement) ? defaultElement : '전체';
  const [element, setElement] = useState(initialElement);
  const [query, setQuery] = useState('');
  const [renderLimit, setRenderLimit] = useState(() => chunkSize(mobile));
  const [scrollActivated, setScrollActivated] = useState(false);
  const listRef = useRef(null);
  const loadMoreRef = useRef(null);
  const visible = useMemo(() => filterRecommendedDecks(decks.decks, element, query), [decks.decks, element, query]);
  const rendered = useMemo(() => visible.slice(0, renderLimit), [renderLimit, visible]);

  useEffect(() => {
    setRenderLimit(chunkSize(mobile));
    setScrollActivated(false);
  }, [element, mobile, query]);

  useEffect(() => {
    const root = listRef.current;
    const node = loadMoreRef.current;
    if (!scrollActivated || !root || !node || renderLimit >= visible.length || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      setRenderLimit((current) => Math.min(visible.length, current + chunkSize(mobile)));
    }, { root, rootMargin: '100px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [mobile, renderLimit, scrollActivated, visible.length]);

  return (
    <div className={`recommended-deck-load-overlay ${mobile ? 'mobile' : 'desktop'}`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}>
      <section className="recommended-deck-load-dialog" role="dialog" aria-modal="true" aria-label="추천 스쿼드 불러오기">
        <header><div><span>RECOMMENDED SQUADS</span><h2>추천 스쿼드</h2></div><button type="button" className="ui-icon-action close" onClick={onClose} aria-label="닫기" title="닫기"><DororaidIcon name="close" size={20} /></button></header>
        <div className="recommended-deck-load-toolbar">
          <div role="tablist" aria-label="추천 속성 필터">{RECOMMENDED_DECK_FILTERS.map((item) => <button type="button" role="tab" aria-selected={element === item} className={element === item ? 'active' : ''} key={item} onClick={() => setElement(item)}>{item}</button>)}</div>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="이름·메모 검색" />
        </div>
        <div ref={listRef} className="recommended-deck-load-list" onScroll={() => setScrollActivated(true)}>
          {decks.loading ? <p className="recommended-deck-load-state">추천덱을 불러오는 중…</p> : null}
          {!decks.loading && catalogLoading && decks.decks.length ? <p className="recommended-deck-load-state">니케 이미지를 준비하는 중…</p> : null}
          {decks.errorMessage ? <div className="recommended-deck-load-state error"><strong>불러오지 못했습니다.</strong><p>{decks.errorMessage}</p><button type="button" onClick={() => decks.refresh()}>다시 시도</button></div> : null}
          {!decks.loading && !decks.errorMessage ? rendered.map((deck, index) => (
            <article key={deck.id}>
              <div><span>{deck.element}</span><h3>{deck.title}</h3><p>{deck.note || '메모 없음'}</p></div>
              <NikkeDeckDisplay
                deck={deck.deck}
                mobile={mobile}
                catalog={catalog}
                loading={index < eagerCardCount(mobile) ? 'eager' : 'lazy'}
                fetchPriority={index === 0 ? 'high' : 'auto'}
                lightweight
              />
              <button type="button" onClick={() => { onApply?.([...deck.deck]); onClose?.(); }}>적용</button>
            </article>
          )) : null}
          {!decks.loading && !decks.errorMessage && !visible.length ? <div className="recommended-deck-load-state"><strong>추천덱이 없습니다.</strong><p>필터를 변경해 다시 확인해주세요.</p></div> : null}
          {renderLimit < visible.length ? <div ref={loadMoreRef} className="recommended-deck-load-more"><button type="button" onClick={() => setRenderLimit((current) => Math.min(visible.length, current + chunkSize(mobile)))}>더 보기 ({Math.min(renderLimit, visible.length)}/{visible.length})</button></div> : null}
        </div>
      </section>
    </div>
  );
}
